import { Line, ScanFields as ScanFieldsType } from '../types';
import { useValidateSerialNumber } from '../hooks/useValidateSerialNumber';
import { useValidatedSnItem } from '../hooks/useValidatedSnItem';
import { useCreateLocator } from '../hooks/useCreateLocator';
import { useContext } from 'react';
import { UserContext } from '../context/UserContext';

interface SerialValidationResult {
  whid: string;
  organizationId: string;
  inventoryItemId: string;
  serialNumber: string;
  itemNumber: string;
  description: string;
  assetNumber: string;
  serialStatus: string;
  snRepairEligible: string;
}

interface SerialNumberScannerProps {
  lines: Line[];
  scanFields: ScanFieldsType;
  onLinesUpdate: (lines: Line[]) => void;
  onScanFieldsUpdate: (fields: ScanFieldsType) => void;
  onSerialNotFound?: (serialNumber: string) => void;
  onMultipleSerialResults?: (results: SerialValidationResult[]) => void;
  selectedHeaderFromSite?: string;
  selectedHeaderToOrg?: string;
  organizationName?: string;
  onLoadingChange?: (loading: boolean) => void;
  onShowWarning?: (message: string) => void;
}

export const useSerialNumberScanner = ({
  lines,
  scanFields,
  onLinesUpdate,
  onScanFieldsUpdate,
  onSerialNotFound,
  onMultipleSerialResults,
  selectedHeaderFromSite,
  selectedHeaderToOrg,
  organizationName,
  onLoadingChange,
  onShowWarning
}: SerialNumberScannerProps) => {
  
  const { validateSerial } = useValidateSerialNumber();
  const { fetchValidatedSnItem } = useValidatedSnItem();
  const { createLocator } = useCreateLocator();
  const { userId } = useContext(UserContext);
  let currentScannedSerial = '';
  
  // Helper function to match serial numbers with/without leading 'S'
  const matchesSerial = (scannedSerial: string, lineSerial: string): boolean => {
    const normalizeSerial = (serial: string) => serial.toLowerCase().replace(/^s/, '');
    const normalizedScanned = normalizeSerial(scannedSerial);
    const normalizedLine = normalizeSerial(lineSerial);
    return normalizedScanned === normalizedLine;
  };
  
  // Helper function to find line by serial number (with S prefix matching)
  const findLineBySerial = (serialNumber: string): number => {
    const index = lines.findIndex(line => {
      const matches = matchesSerial(serialNumber, line.serialNumber || '');
      return matches;
    });
    return index;
  };
  
  const processSerialValidation = async (result: SerialValidationResult, matchingLineIndex: number) => {

    const newLines = [...lines];
    const originalLine = newLines[matchingLineIndex];
    
    // Create locator if all required fields are present
    let locator = scanFields.toLocator;
    if (scanFields.pallet && scanFields.cage && scanFields.tote && scanFields.toSubinventory && selectedHeaderToOrg && organizationName && userId) {
      try {
        const result = await createLocator({
          p_user_id: userId,
          p_organization_name: organizationName,
          p_subinventory: scanFields.toSubinventory,
          p_pallet: scanFields.pallet,
          p_cage: scanFields.cage,
          p_tote: scanFields.tote
        });
        if (result.locator) {
          locator = result.locator;
          console.log('Created locator:', result.locator);
        } else {
          // Create locator failed - show API error message and stop scanning
          if (onShowWarning && result.errorMessage) {
            onShowWarning(result.errorMessage);
          }
          onScanFieldsUpdate({ ...scanFields, scanSerialNumber: "" });
          return; // Stop the scanning process
        }
      } catch (error) {
        console.error('Failed to create locator:', error);
        if (onShowWarning) {
          onShowWarning('Failed to create locator. Please check your pallet, cage, and tote values.');
        }
        onScanFieldsUpdate({ ...scanFields, scanSerialNumber: "" });
        return; // Stop the scanning process
      }
    }
    
    const matchingLine = {
      ...originalLine,
      validatedSerialNumber: result.serialNumber,
      validatedAssetNumber: result.assetNumber,
      validatedSerialItem: result.itemNumber,
      itemNumber: result.itemNumber,
      description: result.description,
      toSubinventory: scanFields.toSubinventory,
      toLocator: locator,
      trackingNumber: scanFields.trackingNumber,
      repairEligibility: result.snRepairEligible === "Serial number not eligible for repair" ? "Serial number not eligible for repair" : "Yes",
      pallet: scanFields.pallet,
      cage: scanFields.cage,
      tote: scanFields.tote,
      toPallet: scanFields.pallet,
      toCage: scanFields.cage,
      toTote: scanFields.tote,
      subinventory: scanFields.toSubinventory
    };
    
    console.log('Updated line:', matchingLine);
    
    // Remove the matching line from its current position
    newLines.splice(matchingLineIndex, 1);
    // Add it to the top
    newLines.unshift(matchingLine);
    
    console.log('New lines array:', newLines);
    onLinesUpdate(newLines);
    onScanFieldsUpdate({ ...scanFields, scanSerialNumber: "" });
  };
  
  const scanSerialNumber = async (serialNumber: string) => {
    console.log('scanSerialNumber called with:', serialNumber);
    console.log('selectedHeaderFromSite:', selectedHeaderFromSite);
    console.log('scanFields:', scanFields);
    console.log('All lines:', lines);
    
    currentScannedSerial = serialNumber; // Store the scanned serial
    
    // Check if serial number exists with Success status or validated serial number (but not Error status)
    const successLine = lines.find(line => {
      const matches = matchesSerial(serialNumber, line.serialNumber);
      const hasSuccessStatus = line.status && line.status.toLowerCase() === 'success';
      const hasValidatedSerial = !!line.validatedSerialNumber;
      const hasErrorStatus = line.status && line.status.toLowerCase() === 'error';
      
      console.log(`Line ${line.id}: serialNumber=${line.serialNumber}, status=${line.status}, validatedSerialNumber=${line.validatedSerialNumber}`);
      console.log(`  - matches: ${matches}, hasSuccessStatus: ${hasSuccessStatus}, hasValidatedSerial: ${hasValidatedSerial}, hasErrorStatus: ${hasErrorStatus}`);
      
      return matches && (hasSuccessStatus || (hasValidatedSerial && !hasErrorStatus));
    });
    
    console.log('Found successLine:', successLine);
    
    if (successLine) {
      if (onShowWarning) {
        onShowWarning('Serial Number has been already received and processed');
      }
      onScanFieldsUpdate({ ...scanFields, scanSerialNumber: "" });
      return;
    }
    
    // Check if serial number exists with Error status - update to New and call upsert API
    const errorLine = lines.find(line => {
      const matches = matchesSerial(serialNumber, line.serialNumber);
      const hasErrorStatus = line.status && line.status.toLowerCase() === 'error';
      
      console.log(`Error check - Line ${line.id}: serialNumber=${line.serialNumber}, status=${line.status}`);
      console.log(`  - matches: ${matches}, hasErrorStatus: ${hasErrorStatus}`);
      
      return matches && hasErrorStatus;
    });
    
    console.log('Found errorLine:', errorLine);
    
    if (errorLine) {
      console.log('Updating error line status to New');
      const updatedLines = lines.map(line => 
        line.id === errorLine.id ? { ...line, status: 'New' } : line
      );
      onLinesUpdate(updatedLines);
      onScanFieldsUpdate({ ...scanFields, scanSerialNumber: "" });
      return;
    }
    
    if (!scanFields.toSubinventory || !scanFields.trackingNumber) {
      console.log('Missing mandatory fields');
      return;
    }

    onLoadingChange?.(true);
    
    // First call getValidatedSnItem API to determine if serial exists in system
    try {
      console.log('Calling getValidatedSnItem API');
      const validatedSnItemResult = await fetchValidatedSnItem(serialNumber);
      console.log('getValidatedSnItem result:', validatedSnItemResult);
      
      if (validatedSnItemResult && validatedSnItemResult.itemNumber.trim()) {
        // getValidatedSnItem returned data - serial exists in system, do NOT open Serial Exception UI
        console.log('Serial exists in system, proceeding with validate-SN API');
        
        // Now call validate-SN API to get repair eligibility and other details
        const validateResults = await validateSerial(selectedHeaderFromSite || '', serialNumber);
        console.log('validate-SN API results:', validateResults);
        
        if (validateResults.length === 1) {
          // Single validate-SN result
          console.log('Processing single validate-SN result');
          const validateResult = validateResults[0];
          
          // Check if serial exists in lines table
          const matchingLineIndex = findLineBySerial(serialNumber);
          if (matchingLineIndex !== -1) {
            console.log('Updating existing line');
            await processSerialValidation(validateResult, matchingLineIndex);
          } else {
            console.log('Creating new line');
            await processPopupSelection(validateResult);
          }
        } else if (validateResults.length > 1) {
          // Multiple validate-SN results - show popup
          console.log('Multiple validate-SN results - showing popup');
          if (onMultipleSerialResults) {
            onMultipleSerialResults(validateResults);
          }
        } else {
          // No validate-SN results - treat as "Not Eligible"
          console.log('No validate-SN results - treating as Not Eligible');
          // Check if serial exists in lines table
          const matchingLineIndex = findLineBySerial(serialNumber);
          if (matchingLineIndex !== -1) {
            // Update existing line with basic data from getValidatedSnItem and "Not Eligible" status
            const basicResult: SerialValidationResult = {
              whid: selectedHeaderFromSite || '',
              organizationId: selectedHeaderToOrg || '',
              inventoryItemId: '',
              serialNumber: serialNumber,
              itemNumber: validatedSnItemResult.itemNumber,
              description: validatedSnItemResult.description,
              assetNumber: validatedSnItemResult.validatedAssetNumber,
              serialStatus: '',
              snRepairEligible: 'Serial number not eligible for repair'
            };
            await processSerialValidation(basicResult, matchingLineIndex);
          } else {
            // Create new line with basic data from getValidatedSnItem and "Not Eligible" status
            const basicResult: SerialValidationResult = {
              whid: selectedHeaderFromSite || '',
              organizationId: selectedHeaderToOrg || '',
              inventoryItemId: '',
              serialNumber: serialNumber,
              itemNumber: validatedSnItemResult.itemNumber,
              description: validatedSnItemResult.description,
              assetNumber: validatedSnItemResult.validatedAssetNumber,
              serialStatus: '',
              snRepairEligible: 'Serial number not eligible for repair'
            };
            await processPopupSelection(basicResult);
          }
        }
      } else {
        // getValidatedSnItem returned no data - serial doesn't exist in system
        // ONLY in this case, open Serial Exception UI
        console.log('getValidatedSnItem returned no data - opening Serial Exception UI');
        
        // Call create-locator API first before opening Serial Exception UI
        if (scanFields.pallet && scanFields.cage && scanFields.tote && scanFields.toSubinventory && selectedHeaderToOrg && organizationName && userId) {
          try {
            const locatorResult = await createLocator({
              p_user_id: userId,
              p_organization_name: organizationName,
              p_subinventory: scanFields.toSubinventory,
              p_pallet: scanFields.pallet,
              p_cage: scanFields.cage,
              p_tote: scanFields.tote
            });
            if (!locatorResult.locator) {
              if (onShowWarning && locatorResult.errorMessage) {
                onShowWarning(locatorResult.errorMessage);
              }
              onScanFieldsUpdate({ ...scanFields, scanSerialNumber: "" });
              return;
            }
          } catch (error) {
            console.error('Failed to create locator before opening Serial Exception UI:', error);
            if (onShowWarning) {
              onShowWarning('Failed to create locator. Please check your pallet, cage, and tote values.');
            }
            onScanFieldsUpdate({ ...scanFields, scanSerialNumber: "" });
            return;
          }
        }
        
        // Open Serial Exception UI
        if (onSerialNotFound) {
          onSerialNotFound(serialNumber);
        }
      }
    } catch (error) {
      console.error('Error in serial number validation process:', error);
    } finally {
      onLoadingChange?.(false);
    }
  };

  const processPopupSelection = async (selectedResult: SerialValidationResult) => {
    console.log('processPopupSelection called with:', selectedResult);
    console.log('currentScannedSerial:', currentScannedSerial);
    console.log('lines:', lines);
    
    // Check if the selected serial number exists in lines table
    const matchingLineIndex = findLineBySerial(selectedResult.serialNumber);
    
    console.log('matchingLineIndex:', matchingLineIndex);
    
    if (matchingLineIndex !== -1) {
      // Update existing line
      console.log('Updating existing line');
      await processSerialValidation(selectedResult, matchingLineIndex);
    } else {
      // No matching line - create new line
      console.log('Creating new line');
      
      // Create locator if all required fields are present
      let locator = scanFields.toLocator;
      if (scanFields.pallet && scanFields.cage && scanFields.tote && scanFields.toSubinventory && selectedHeaderToOrg && organizationName && userId) {
        try {
          const locatorResult = await createLocator({
            p_user_id: userId,
            p_organization_name: organizationName,
            p_subinventory: scanFields.toSubinventory,
            p_pallet: scanFields.pallet,
            p_cage: scanFields.cage,
            p_tote: scanFields.tote
          });
          if (locatorResult.locator) {
            locator = locatorResult.locator;
            console.log('Created locator for new line:', locatorResult.locator);
          } else {
            // Create locator failed - show API error message and stop
            if (onShowWarning && locatorResult.errorMessage) {
              onShowWarning(locatorResult.errorMessage);
            }
            onScanFieldsUpdate({ ...scanFields, scanSerialNumber: "" });
            return; // Stop the process
          }
        } catch (error) {
          console.error('Failed to create locator for new line:', error);
          if (onShowWarning) {
            onShowWarning('Failed to create locator. Please check your pallet, cage, and tote values.');
          }
          onScanFieldsUpdate({ ...scanFields, scanSerialNumber: "" });
          return; // Stop the process
        }
      }
      
      // Create new line with selected result data
      const newLine: Line = {
        id: `new-${Date.now()}`,
        serialNumber: selectedResult.serialNumber,
        validatedSerialNumber: selectedResult.serialNumber,
        validatedAssetNumber: selectedResult.assetNumber,
        validatedSerialItem: selectedResult.itemNumber,
        itemNumber: selectedResult.itemNumber,
        description: selectedResult.description,
        repairEligibility: selectedResult.snRepairEligible === "Serial number not eligible for repair" ? "Serial number not eligible for repair" : "Yes",
        status: 'New',
        toSubinventory: scanFields.toSubinventory,
        toLocator: locator,
        trackingNumber: scanFields.trackingNumber,
        pallet: scanFields.pallet,
        cage: scanFields.cage,
        tote: scanFields.tote,
        toPallet: scanFields.pallet,
        toCage: scanFields.cage,
        toTote: scanFields.tote,
        subinventory: scanFields.toSubinventory,
        quantity: 1,
        uom: 'EA',
        assetNumber: selectedResult.assetNumber,
        internalSalesOrder: '',
        isoLineNumber: '',
        replacementOrderLineStatus: ''
      };
      
      // Add new line to top of the list
      const newLines = [newLine, ...lines];
      onLinesUpdate(newLines);
      
      console.log('Created new line:', newLine);
    }
    
    // Clear scan field
    onScanFieldsUpdate({ ...scanFields, scanSerialNumber: "" });
  };

  return {
    scanSerialNumber,
    processSerialValidation,
    processPopupSelection
  };
};